import React from 'react'

import { TaskCommentsEditContextType, defaultValue } from './helpers'

import { useTaskComments } from '../TaskCommentsProvider'
import { useAreYouSure } from '../AreYouSureProvider'
import { useTaskAPI } from '../TaskAPIProvider'

type TaskCommentsEditProviderProps = {
  taskID: string
  children: React.ReactNode
}

const TaskCommentsEditContext =
  React.createContext<TaskCommentsEditContextType>(null)

const TaskCommentsEditProvider: React.FC<TaskCommentsEditProviderProps> = ({
  taskID,
  children
}) => {
  const { updateComment } = useTaskComments()
  const { setV2, setTitle, setReason, setSubtext, setAction } = useAreYouSure()
  const { deleteComment: deleteTaskComment } = useTaskAPI()

  const [content, setContent] = React.useState('')
  const [editingID, setEditingID] = React.useState('')
  const editing = React.useMemo(() => editingID.length > 0, [editingID.length])
  const [startingContent, setStartingContent] = React.useState('')

  const resetState = React.useCallback(() => {
    setContent('')
    setEditingID('')
    setStartingContent('')
  }, [])

  const startEdit: TaskCommentsEditContextType['startEdit'] = React.useCallback(
    comment => {
      setContent(comment.content)
      setEditingID(comment.id)
      setStartingContent(comment.content)
    },
    []
  )

  const saveEdit: TaskCommentsEditContextType['saveEdit'] =
    React.useCallback(() => {
      if (editingID) {
        if (content !== startingContent) {
          updateComment(taskID, editingID, content)
        }

        resetState()
      }
    }, [editingID, content, startingContent, updateComment, taskID, resetState])

  const discardEdit: TaskCommentsEditContextType['discardEdit'] =
    React.useCallback(() => {
      resetState()
    }, [resetState])

  const deleteComment: TaskCommentsEditContextType['deleteComment'] =
    React.useCallback(
      commentId => {
        setV2(true)
        setTitle('DELETE COMMENT?')
        setReason('Are you sure you want to delete this comment?')
        setSubtext('This cannot be undone.')
        setAction(() => {
          deleteTaskComment(taskID, commentId)
        })
      },
      [
        setV2,
        setTitle,
        setReason,
        setSubtext,
        setAction,
        deleteTaskComment,
        taskID
      ]
    )

  const updateContent: TaskCommentsEditContextType['updateContent'] =
    React.useCallback(text => {
      setContent(text)
    }, [])

  return (
    <TaskCommentsEditContext.Provider
      value={{
        editing,
        content,
        editingID,
        startEdit,
        saveEdit,
        discardEdit,
        deleteComment,
        updateContent
      }}
    >
      {children}
    </TaskCommentsEditContext.Provider>
  )
}

export default TaskCommentsEditProvider

export const useTaskCommentsEdit = () =>
  React.useContext(TaskCommentsEditContext) ?? defaultValue
